import { useBoolean, useConst } from '@fluentui/react-hooks';
import * as signalR from "@microsoft/signalr";
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { Data } from '../data/Data';
import { AccessType, useAuth } from './authContext';
import { useDocumentsManager } from './documentContext';
import { useMessagesManager } from './messageContext';
import { useNotificationsManager } from './notificationContext';
import { useUser } from './userContext';

const TchatContext = createContext();
const TchatManagerContext = createContext();

export const TchatContextProvider = ({ children }) => {

    const { accessType, isAuthenticated } = useAuth();
    const { addAttachment } = useDocumentsManager();
    const { addMessage } = useMessagesManager();
    const { extractErrorMessages } = useNotificationsManager();
    const { user } = useUser();

    const defaultMessage = useConst({
        fileData: null,
        fileName: null,
        idMessageParent: null,
        texte: "",
        texteMessageParent: null,
    });

    const [hubConnection, setHubConnection] = useState(undefined);
    const [message, setMessage] = useState({ ...defaultMessage });
    const [sendingInProgress, { setTrue: blockInputs, setFalse: allowInputs }] = useBoolean(false);
    const [isOnline, { setTrue: setOnline, setFalse: setOffline }] = useBoolean(false);

    const onCloseConnection = useCallback((error) => {
        setOffline();
    }, [setOffline]);

    // Préparation de la connexion au hub de messagerie
    useEffect(() => {
        if (isAuthenticated && accessType === AccessType.client && !hubConnection)
            Data._getUrlTchatHub().then(response => {

                const url = response.reponseAPIobjet;
                //const url = "https://localhost:44370";

                const origin = window.location.origin;
                let logLevel = signalR.LogLevel.None
                if (origin.includes("localhost")) logLevel = signalR.LogLevel.Debug;
                else if (origin.includes("recette")) logLevel = signalR.LogLevel.Debug;
                else if (origin.includes("preprod")) logLevel = signalR.LogLevel.Debug;

                let hubConnection = new signalR.HubConnectionBuilder()
                    .withUrl(`${url}/tchat`)
                    .withAutomaticReconnect()
                    .configureLogging(logLevel)
                    .build();

                setHubConnection(hubConnection);

            }).catch(err => console.log("Impossible d'établir la connexion avec le serveur."));
    }, [accessType, hubConnection, isAuthenticated]);

    // Connexion au hub de messagerie
    useEffect(() => {
        const { Disconnected } = signalR.HubConnectionState;
        if (isAuthenticated && !!hubConnection && hubConnection.state === Disconnected) {
            const { idContactChiffre } = user;
            hubConnection
                .start()
                .then(() => {
                    hubConnection.invoke("Connect", idContactChiffre, false);
                    setOnline();
                })
                .catch(error => console.error(error));
            hubConnection.on("NouveauMessage", addMessage);
            hubConnection.onclose(onCloseConnection);
            hubConnection.onreconnecting(error => console.log(error));
            hubConnection.onreconnected(id => console.log("Reconnected"));
        }
    }, [addMessage, hubConnection, isAuthenticated, onCloseConnection, setOnline, user]);

    // Déconnexion du hub de messagerie
    useEffect(() => {
        if (!isAuthenticated && !!hubConnection) {
            const { Disconnected, Disconnecting } = signalR.HubConnectionState;
            const { state } = hubConnection;
            if (state !== Disconnected && state !== Disconnecting)
                if (typeof hubConnection.stop === "function")
                    hubConnection
                        .stop()
                        .then(() => console.log("Déconnecté du serveur de messagerie"))
                        .catch(error => console.error(error));
        }
    }, [hubConnection, isAuthenticated]);

    const updateMessage = useCallback((updates) => {
        return setMessage(prev => ({ ...prev, ...updates }));
    }, []);

    const resetAttachment = useCallback(() => {
        return setMessage(prev => ({
            ...prev,
            fileData: defaultMessage.fileData,
            fileName: defaultMessage.fileName
        }));
    }, [defaultMessage]);

    const resetMessage = useCallback(() => {
        return setMessage({ ...defaultMessage });
    }, [defaultMessage]);

    const resetParent = useCallback(() => {
        return setMessage(prev => ({
            ...prev,
            idMessageParent: defaultMessage.idMessageParent,
            texteMessageParent: defaultMessage.texteMessageParent
        }));
    }, [defaultMessage]);

    const sendMessage = useCallback(async () => {
        if (!hubConnection) return;
        else {
            blockInputs();

            const { idContactChiffre } = user;
            const { texte, fileName, fileData, idMessageParent, texteMessageParent, loginCompte, idOperation, idReservation } = message;
            const response = await hubConnection.invoke("EnvoyerMessage", idContactChiffre, texte, fileName, fileData, idMessageParent, texteMessageParent, loginCompte, idOperation, idReservation)
            if (response.codeRetour === 0) {
                const newMessage = response.reponseAPIobjet;
                addMessage(newMessage);

                if (Array.isArray(newMessage.piecesJointes)) {
                    const { rowGUID } = newMessage.piecesJointes[0];
                    addAttachment(rowGUID, fileData, fileName);
                }

                resetMessage();
            }
            else extractErrorMessages(response);

            allowInputs();
        }
    }, [addAttachment, addMessage, allowInputs, blockInputs, extractErrorMessages, hubConnection, message, resetMessage, user]);

    const value = useMemo(
        () => ({ isOnline, message, sendingInProgress }),
        [isOnline, message, sendingInProgress]
    );

    const managerValue = useMemo(
        () => ({ resetAttachment, resetParent, resetMessage, sendMessage, updateMessage }),
        [resetAttachment, resetParent, resetMessage, sendMessage, updateMessage]
    );

    return <TchatManagerContext.Provider value={managerValue}>
        <TchatContext.Provider value={value}>
            {children}
        </TchatContext.Provider>
    </TchatManagerContext.Provider>;

};

export const useTchat = () => {
    const data = React.useContext(TchatContext);
    if (data === undefined) throw new Error();
    const { isOnline, message, sendingInProgress } = data;
    return { isOnline, message, sendingInProgress };
};

export const useTchatManager = () => {
    const manager = React.useContext(TchatManagerContext);
    if (manager === undefined) throw new Error();
    const { resetAttachment, resetParent, resetMessage, sendMessage, updateMessage } = manager;
    return { resetAttachment, resetParent, resetMessage, sendMessage, updateMessage };
};